<template>
  <InfusePageLayout>
    <div class="flex flex-col gap-6 overflow-y-auto min-h-[30vh]">
      <MessageTable
        :has-more-items="hasNextPage"
        :page="currentPage"
        :loading="isLoading"
        :rows="messages"
        :date-range="filterOptions.range"
        :filters="filters"
        :filter-configs="filterConfigs"
        :search-enabled="false"
        @click-row="(row: ApiMessage) => redirectToDetails(router, row.id, 'messages.charging-station-message')"
        @load="fetchNextPage"
        @add:filter="(f) => onAddFilter(f)"
        @remove:filter="(f) => onRemoveFilter(f)"
        @update:date-range="onDateRangeChange"
      />
    </div>
  </InfusePageLayout>
</template>
<script setup lang="ts">
import { MessageTable } from '@infuse/e-connector/message/ui';
import { LogMessageDateRange, Message as ApiMessage, MessageType } from '@infuse/api/e-connector/message';
import { InfusePageLayout } from '@infuse/ui/layouts';
import { computed, onMounted, reactive } from 'vue';
import { useRouter } from 'vue-router';
import { redirectToDetails } from '@infuse/e-connector/shared/utils';
import { Filter, FilterConfiguration, FilterConfigurationType, FilterOption } from '@infuse/ui/domain';
import { FilterCollection } from '@infuse/cpms/charging-station/filters';
import { FilterExpression } from '@infuse/api/common';
import { Ocpp1xMessageAction } from '@infuse/cpms/charging-station/store';
import { useI18n } from 'vue-i18n';
import { useMessages } from '@infuse/e-connector/message/queries';
import { useChargingStations } from '@infuse/e-connector/charging-station/queries';
import { useCentralSystems } from '@infuse/e-connector/central-system/queries';
import { ECONNECTOR_MAX_PAGE_SIZE } from '@infuse/e-connector/shared/queries';

const router = useRouter();
const { t } = useI18n();

const { messages, isLoading, hasNextPage, currentPage, fetchNextPage, filterOptions } = useMessages();
const { chargingStations, fetchAllPages: fetchAllChargingStations } = useChargingStations(ECONNECTOR_MAX_PAGE_SIZE);
const { centralSystems, fetchAllPages: fetchAllCentralSystems } = useCentralSystems(ECONNECTOR_MAX_PAGE_SIZE);

// Filters UI state
const filterCollection = reactive(new FilterCollection());
const filters = computed<Filter[]>(() => filterCollection.filters);

// Predefined lists
const PREDEFINED_TYPES: string[] = Object.values(MessageType);
const PREDEFINED_VERSIONS: string[] = ['1.5', '1.6'];
const PREDEFINED_ACTIONS: string[] = Object.values(Ocpp1xMessageAction);

// Helper to create FilterOption[] from string[]
const toOptions = (values: string[]): FilterOption[] =>
  values
    .filter((v) => !!v)
    .sort()
    .map((v) => ({
      name: v,
      label: () => v,
      toUmbrellaExpression: () => new FilterExpression()
    }));

// Helper specifically for translating MessageType options
const toTypeOptions = (values: string[]): FilterOption[] =>
  values
    .filter((v) => !!v)
    .sort()
    .map((v) => ({
      name: v,
      label: () => t(`e_connector.messages.filterOptions.type.${v}`),
      toUmbrellaExpression: () => new FilterExpression()
    }));

const filterConfigs = computed<FilterConfiguration[]>(() => {
  const configs = [
    {
      name: 'types',
      label: () => t('e_connector.messages.filters.type'),
      type: FilterConfigurationType.SELECT,
      options: toTypeOptions(PREDEFINED_TYPES)
    },
    {
      name: 'versions',
      label: () => t('e_connector.messages.filters.version'),
      type: FilterConfigurationType.SELECT,
      options: toOptions(PREDEFINED_VERSIONS)
    },
    {
      name: 'actions',
      label: () => t('e_connector.messages.filters.action'),
      type: FilterConfigurationType.SELECT,
      options: toOptions(PREDEFINED_ACTIONS)
    }
  ];

  if (chargingStations.value.length > 0) {
    const chargingStationNames = chargingStations.value.map((cs) => cs.name);
    configs.push({
      name: 'chargePoints',
      label: () => t('e_connector.messages.filters.chargingStation'),
      type: FilterConfigurationType.SEARCH_SELECT,
      options: toOptions(chargingStationNames)
    });
  }

  if (centralSystems.value.length > 0) {
    const centralSystemNames = centralSystems.value.map((cs) => cs.name);
    configs.push({
      name: 'centralSystems',
      label: () => t('e_connector.messages.filters.centralSystem'),
      type: FilterConfigurationType.SEARCH_SELECT,
      options: toOptions(centralSystemNames)
    });
  }

  return configs;
});

onMounted(async () => {
  filterOptions.value.range = LogMessageDateRange.LAST_6_WEEKS;
  await fetchAllChargingStations();
  await fetchAllCentralSystems();
});

function applyFiltersToQuery() {
  const current = filters.value;
  filterOptions.value.types = current.filter((f) => f.config.name === 'types').map((f) => f.selectedOption.name as MessageType);
  filterOptions.value.versions = current.filter((f) => f.config.name === 'versions').map((f) => f.selectedOption.name as string);
  filterOptions.value.actions = current.filter((f) => f.config.name === 'actions').map((f) => f.selectedOption.name as string);
  filterOptions.value.chargePoints = current.filter((f) => f.config.name === 'chargePoints').map((f) => f.selectedOption.name as string);
  filterOptions.value.centralSystems = current
    .filter((f) => f.config.name === 'centralSystems')
    .map((f) => f.selectedOption.name as string);
}

async function onAddFilter(f: Filter) {
  filterCollection.add(f);
  applyFiltersToQuery();
}

async function onRemoveFilter(f: Filter) {
  filterCollection.remove(f);
  applyFiltersToQuery();
}

function onDateRangeChange(newDateRange: LogMessageDateRange) {
  filterOptions.value.range = newDateRange;
}
</script>
